import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import GroupLayer from "@arcgis/core/layers/GroupLayer";
import SketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
import React, { useEffect, useRef, useState } from "react";
import { line, polygon } from "./symbols";
import Point from "@arcgis/core/geometry/Point";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine.js";
import { v4 as uuidv4 } from "uuid";
import {
  addLabelsToGeometry,
  getBearing,
  removefeatureLabelByFeatureId,
  convertMeasurement,
  unitDict,
} from "./MeasurementVM";
import {
  Box,
  Button,
  FormControl,
  FormGroup,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Toolbar,
  Typography,
} from "@mui/material";
import SnappingControlsWidget from "../snapping-controls/SnappingControlsWidget";
import GeometryButton from "../../../components/geometry-buttons/GeometryButton";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import Stack from "@mui/material/Stack";
const MeasurementWidget = ({ view, activeWidget,page }) => {
  const [activeGeometry, setActiveGeometry] = useState<string>("");
  const [totalDistance, setTotalDistance] = useState<number>(0);
  const [area, setArea] = useState<number>(0);
  const [radius, setRadius] = useState<number>(0);
  const [currentBearing, setCurrentBearing] = useState<string>("");
  const [currentSegmentLength, setCurrentSegmentLength] = useState<number>(0);
  const [currentSourceUnit] = useState("feet");
  const [prevTargetLengthUnit, setPrevTargetLengthUnit] = useState("feet");
  const [prevTargetAreaUnit, setPrevTargetAreaUnit] = useState("feet");
  const [currentTargetLengthUnit, setCurrentTargetLengthUnit] =
    useState("feet");
  const [currentTargetAreaUnit, setCurrentTargetAreaUnit] = useState("feet");

  const mouseMoveEventHandler = useRef(null);
  const [measurementLabelLayer] = useState<GraphicsLayer>(
    new GraphicsLayer({
      id: "measure_label_graphiclyr",
      title: "Measurement Label Layer",
      listMode: "hide",
    })
  );
  const [visible, setVisible] = useState(false);
  const measureToolWidgetDiv = useRef(null);
  const [sketchViewModel,setSketchViewModel] = useState(null);
  const measureToolPopup = useRef(null);
  const [measurementLayer] = useState<GraphicsLayer>(
    new GraphicsLayer({
      id: "measure_graphiclyr",
      title: "Measurement Layer",
      listMode: "hide",
    })
  );
  const [screenPositionX, setScreenPositionX] = useState(0);
  const [screenPositionY, setScreenPositionY] = useState(0);
  const [beginSegmentCoordinates, setBeginSegmentCoordinates] = useState([]);
  const [endSegmentCoordinates, setEndSegmentCoordinates] = useState([]);
  const [eventArray, setEventArray] = useState([]);

  useEffect(() => {
    if (measureToolWidgetDiv.current && measureToolPopup.current) {
      view.ui.add(measureToolPopup.current, "manual");
      let layersArray = view.map.layers.filter(
        (layer) => layer.id === "operationalLayers"
      );

      if (layersArray.length > 0) {
        let opGroupLayer = layersArray.getItemAt(0) as GroupLayer;
        opGroupLayer.add(measurementLayer);
        opGroupLayer.add(measurementLabelLayer);
      }

      let sketchVM = new SketchViewModel({
        view: view,
        layer: measurementLayer,
        defaultCreateOptions:{mode:"hybrid"},
        polylineSymbol: line as any,
        polygonSymbol: polygon as any,
        defaultUpdateOptions: { toggleToolOnClick: false, tool: "transform" },
        updateOnGraphicClick: true,
      });
      setSketchViewModel(sketchVM);
    }
  }, []);

  useEffect(() => {
    setArea((prev) =>
      convertMeasurement(prev, prevTargetAreaUnit, currentTargetAreaUnit, true)
    );
  }, [currentTargetAreaUnit, prevTargetAreaUnit]);

  useEffect(() => {
    setTotalDistance((prev) =>
      convertMeasurement(
        prev,
        prevTargetLengthUnit,
        currentTargetLengthUnit,
        false
      )
    );
    setCurrentSegmentLength((prev) =>
      convertMeasurement(
        prev,
        prevTargetLengthUnit,
        currentTargetLengthUnit,
        false
      )
    );
    setRadius((prev) => {
      return convertMeasurement(
        prev,
        prevTargetLengthUnit,
        currentTargetLengthUnit,
        false
      );
    });
  }, [prevTargetLengthUnit, currentTargetLengthUnit]);

  useEffect(() => {
    if (sketchViewModel && measurementLabelLayer) {
      let featureId = "";
      let eventList = [];
      eventArray.forEach((e) => e.remove());
      eventList.push(
        sketchViewModel.on("create", (event) => {
          const { state, toolEventInfo, tool, graphic } = event;
          if (toolEventInfo?.type === "vertex-add") {
            setBeginSegmentCoordinates(toolEventInfo.vertices[0].coordinates);
          }
          if (toolEventInfo?.type === "cursor-update") {
            setEndSegmentCoordinates(toolEventInfo.coordinates);
          }
          if (state === "start") {
            featureId = uuidv4();
          }
          if (state === "active") {
            if (tool === "polyline" || tool === "polygon") {
              const perimeterDistance = parseFloat(
                geometryEngine.planarLength(event.graphic.geometry).toFixed(2)
              );
              setTotalDistance(
                convertMeasurement(
                  perimeterDistance,
                  currentSourceUnit,
                  currentTargetLengthUnit,
                  false
                )
              );
            }

            if (tool !== "polyline") {
              setArea(
                convertMeasurement(
                  parseFloat(
                    geometryEngine.planarArea(graphic.geometry).toFixed(2)
                  ),
                  currentSourceUnit,
                  currentTargetAreaUnit,
                  true
                )
              );
            }
          }
          if (state === "complete") {
            graphic.attributes = { featureId: featureId, tool: tool };
            addLabelsToGeometry(
              graphic,
              measurementLabelLayer,
              view.spatialReference,
              currentTargetLengthUnit,
              currentTargetAreaUnit
            );
            //}
            if (
              tool === "polygon" ||
              tool === "circle" ||
              tool === "rectangle"
            ) {
              setArea(
                convertMeasurement(
                  parseFloat(
                    geometryEngine.planarArea(graphic.geometry).toFixed(2)
                  ),
                  currentSourceUnit,
                  currentTargetAreaUnit,
                  true
                )
              );
            }
            if (tool === "circle") {
              let lastVertex = new Point({
                x: graphic.geometry.rings[0][
                  graphic.geometry.rings[0].length - 1
                ][0],
                y: graphic.geometry.rings[0][
                  graphic.geometry.rings[0].length - 1
                ][1],
                spatialReference: view.spatialReference,
              });
              setRadius(
                convertMeasurement(
                  parseFloat(
                    geometryEngine
                      .distance(graphic.geometry.centroid, lastVertex)
                      .toFixed(2)
                  ),
                  currentSourceUnit,
                  currentTargetLengthUnit
                )
              );
            }
          }
        })
      );
      eventList.push(
        sketchViewModel.on("update", (event) => {
          const { state, toolEventInfo, tool, graphics } = event;
          if (state === "start" || state === "active") {
            graphics.forEach((graphic) => {
              removefeatureLabelByFeatureId(
                measurementLabelLayer,
                graphic.attributes.featureId
              );
              setCurrentBearing("");
              setActiveGeometry(graphic.attributes.tool);
              if (graphic.attributes.tool === "polyline") {
                setArea(0);
              }
              if (
                graphic.attributes.tool === "polyline" ||
                graphic.attributes.tool === "polygon"
              ) {
                const perimeterDistance = parseFloat(
                  geometryEngine.planarLength(graphic.geometry).toFixed(2)
                );
                setTotalDistance(
                  convertMeasurement(
                    perimeterDistance,
                    currentSourceUnit,
                    currentTargetLengthUnit,
                    false
                  )
                );
              }
              if (
                graphic.attributes.tool === "polygon" ||
                graphic.attributes.tool === "circle" ||
                graphic.attributes.tool === "rectangle"
              ) {
                setArea(
                  convertMeasurement(
                    parseFloat(
                      geometryEngine.planarArea(graphic.geometry).toFixed(2)
                    ),
                    currentSourceUnit,
                    currentTargetAreaUnit,
                    true
                  )
                );
              }
              if (graphic.attributes.tool === "circle") {
                let lastVertex = new Point({
                  x: graphic.geometry.rings[0][
                    graphic.geometry.rings[0].length - 1
                  ][0],
                  y: graphic.geometry.rings[0][
                    graphic.geometry.rings[0].length - 1
                  ][1],
                  spatialReference: view.spatialReference,
                });
                setRadius(
                  convertMeasurement(
                    parseFloat(
                      geometryEngine
                        .distance(graphic.geometry.centroid, lastVertex)
                        .toFixed(2)
                    ),
                    currentSourceUnit,
                    currentTargetLengthUnit
                  )
                );
              } else {
                // setRadius(0)
              }
            });
          }
          if (state === "active") {
          }
          if (state === "complete") {
            // if(tool==='polyline'){
            graphics.forEach((graphic) => {
              addLabelsToGeometry(
                graphic,
                measurementLabelLayer,
                view.spatialReference,
                currentTargetLengthUnit,
                currentTargetAreaUnit
              );
            });
            //}
          }
        })
      );

      eventList.push(
        sketchViewModel.on("redo", (event) => {
        })
      );
      eventList.push(
        sketchViewModel.on("delete", (event) => {
          event.graphics.forEach((graphic) => {
            let featureId = graphic?.attributes?.featureId;
            let featureLabelsList = measurementLabelLayer.graphics
              .filter((label) => {
                return label.attributes.featureId === featureId;
              })
              .toArray();
            if (featureLabelsList.length > 0) {
              measurementLabelLayer.removeMany(featureLabelsList);
            }
          });
        })
      );
      eventList.push(
        sketchViewModel.on("undo", (event) => {
        })
      );
      setEventArray(eventList);
    }
  }, [sketchViewModel,measurementLabelLayer, currentTargetLengthUnit, currentTargetAreaUnit]);

  useEffect(() => {
    if (
      beginSegmentCoordinates.length === 2 &&
      endSegmentCoordinates.length === 2 &&
      beginSegmentCoordinates[0] !== endSegmentCoordinates[0] &&
      beginSegmentCoordinates[1] !== endSegmentCoordinates[1]
    ) {
      let prevVertex = new Point({
        x: beginSegmentCoordinates[0],
        y: beginSegmentCoordinates[1],
        spatialReference: view.spatialReference,
      });
      let lastVertex = new Point({
        x: endSegmentCoordinates[0],
        y: endSegmentCoordinates[1],
        spatialReference: view.spatialReference,
      });
      setCurrentSegmentLength(
        convertMeasurement(
          parseFloat(
            geometryEngine.distance(prevVertex, lastVertex).toFixed(2)
          ),
          currentSourceUnit,
          currentTargetLengthUnit
        )
      );
      setCurrentBearing(getBearing(prevVertex, lastVertex));
      if (activeGeometry === "circle") {
        setRadius(
          convertMeasurement(
            parseFloat(
              geometryEngine.distance(prevVertex, lastVertex).toFixed(2)
            ),
            currentSourceUnit,
            currentTargetLengthUnit
          )
        );
      }
    }
  }, [
    beginSegmentCoordinates,
    endSegmentCoordinates,
    currentTargetAreaUnit,
    currentTargetLengthUnit,
    currentSourceUnit,
    activeGeometry,
  ]);

  useEffect(() => {
    if (activeWidget === "measurementTool" && page==='tools') {
      setVisible(true);
      mouseMoveEventHandler.current = view.on("pointer-move", (e) => {
        setScreenPositionX(Math.round(e.x));
        setScreenPositionY(Math.round(e.y));
      });
    } else {
      setVisible(false);
      if (mouseMoveEventHandler.current) {
        mouseMoveEventHandler.current.remove();
        mouseMoveEventHandler.current=""
        setScreenPositionX(0);
        setScreenPositionY(0);
      }
    }
  }, [activeWidget,page]);

  const handleGeometryChange = (event) => {
    const { value } = event.target;
    sketchViewModel.create(value);
    setActiveGeometry(value);
  };
  return (
    <>
      <Box>
        <div
          ref={measureToolWidgetDiv}
          style={visible ? { display: "initial" } : { display: "none" }}
        >
          <Stack direction="row" spacing={1} sx={{flexWrap:"wrap"}}>
            <GeometryButton
              value="polyline"
              title="measure length"
              id="measure-line"
              onClick={(e) => handleGeometryChange(e)}
            />

            <GeometryButton
              value="polygon"
              title="measure area"
              id="measure-polygon"
              onClick={(e) => handleGeometryChange(e)}
            />
            <GeometryButton
              value="circle"
              title="measure circle"
              id="measure-circle"
              onClick={(e) => handleGeometryChange(e)}
            />

            <GeometryButton
              value="rectangle"
              title="measure rectangle"
              id="measure-rectangle"
              onClick={(e) => handleGeometryChange(e)}
            />

            <IconButton
              title="clear selected"
              onClick={(e) => {
                sketchViewModel.delete();
              }}
            >
              <DeleteIcon />
            </IconButton>

            <IconButton
              title="clear all"
              onClick={(e) => {
                measurementLayer.removeAll();
                measurementLabelLayer.removeAll();
              }}
            >
              <DeleteSweepIcon />
            </IconButton>
            </Stack>
          <FormLabel component="legend">Select unit of measure</FormLabel>
          <Stack direction="row" spacing={2}>
            <FormGroup row>
              <FormControl size="small" sx={{ mt: 2, mr: 2 }}>
                <InputLabel id="demo-simple-select-label">Length</InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={currentTargetLengthUnit}
                  label="Age"
                  onChange={(e) => {
                    setCurrentTargetLengthUnit((prev) => {
                      setPrevTargetLengthUnit(prev);
                      return e.target.value;
                    });
                  }}
                >
                  <MenuItem value={"feet"}>feet</MenuItem>
                  <MenuItem value={"centimeters"}>centimeters</MenuItem>
                  <MenuItem value={"meters"}>meters</MenuItem>
                  <MenuItem value={"kilometers"}>kilometers</MenuItem>
                  <MenuItem value={"inches"}>inches</MenuItem>
                  <MenuItem value={"yards"}>yards</MenuItem>
                  <MenuItem value={"miles"}>miles</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ mt: 2, mr: 2 }}>
                <InputLabel id="select-area-label">Area</InputLabel>
                <Select
                  labelId="select-area-label"
                  id="select-area-unit"
                  value={currentTargetAreaUnit}
                  label="area"
                  onChange={(e) => {
                    setCurrentTargetAreaUnit((prev) => {
                      setPrevTargetAreaUnit(prev);
                      return e.target.value;
                    });
                  }}
                >
                  <MenuItem value={"feet"}>feet</MenuItem>
                  <MenuItem value={"centimeters"}>centimeters</MenuItem>
                  <MenuItem value={"meters"}>meters</MenuItem>
                  <MenuItem value={"kilometers"}>kilometers</MenuItem>
                  <MenuItem value={"inches"}>inches</MenuItem>
                  <MenuItem value={"yards"}>yards</MenuItem>
                  <MenuItem value={"miles"}>miles</MenuItem>
                  <MenuItem value={"acres"}>acres</MenuItem>
                </Select>
              </FormControl>
            </FormGroup>
          </Stack>
          {sketchViewModel && <SnappingControlsWidget
            view={view}
            activeWidget="snappingOptions"
            sketchViewModel={sketchViewModel}
            handleName="measurement"
          ></SnappingControlsWidget>}
        </div>
      </Box>
      <Paper
        ref={measureToolPopup}
        sx={
          screenPositionX && screenPositionY
            ? {
                display: "initial",
                padding: "8px",
                opacity: 0.8,
                position: "absolute",
                top: `${screenPositionY + 20}px`,
                left: `${screenPositionX + 20}px`,
              }
            : { display: "none" }
        }
        variant="outlined"
      >
        {activeGeometry !== "rectangle" &&
        activeGeometry !== "circle" &&
        currentSegmentLength ? (
          <Typography variant="body1">
            segment length:
            {(Math.round(currentSegmentLength * 100) / 100).toLocaleString()}{" "}
            {unitDict[currentTargetLengthUnit]}
          </Typography>
        ) : (
          ""
        )}
        {currentBearing && (
          <Typography variant="body1">bearing:{currentBearing}</Typography>
        )}
        {activeGeometry !== "rectangle" && activeGeometry !== "circle" ? (
          <Typography variant="body1">
            total distance:
            {(Math.round(totalDistance * 100) / 100).toLocaleString()}{" "}
            {unitDict[currentTargetLengthUnit]}
          </Typography>
        ) : (
          ""
        )}
        {activeGeometry !== "polyline" && (
          <Typography variant="body1">
            area:{(Math.round(area * 100) / 100).toLocaleString()}{" "}
            {unitDict[currentTargetAreaUnit]}
          </Typography>
        )}
        {activeGeometry === "circle" && (
          <Typography variant="body1">
            radius:{(Math.round(radius * 100) / 100).toLocaleString()}{" "}
            {unitDict[currentTargetLengthUnit]}
          </Typography>
        )}
      </Paper>
    </>
  );
};

export default MeasurementWidget;
